import { useState, useCallback, useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
} from "react-native";
import {
  ChevronLeft,
  ClipboardCheck,
  ChevronRight,
  HeartPulse,
  Users,
  Layers,
  Sparkles,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  getBlock,
  makeId,
  type ContentBlock,
  type ContentBlockId,
  type ProgramCategory,
} from "@src/packages/content";
import { useFertilityIntent } from "@src/packages/fertility-intent";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import ProgramCardIllustration, {
  type ProgramCardIllustrationKind,
} from "../../components/access/ProgramCardIllustration";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CardSubtitleText,
  MoonFilterChip,
  SectionTitle,
} from "@louise/moon-ui";
import {
  colors,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import StatusBadge from "../../components/StatusBadge";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import type { Patient } from "@src/packages/patients/patients.types";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type HadVaricoceles = NonNullable<Patient["medicalHistory"]>["hadVaricoceles"];

function formatFixedPrice(amount: number, currency: string): string {
  return currency === "USD"
    ? `$${amount.toLocaleString()}`
    : `${amount.toLocaleString()} ${currency}`;
}

function withoutEmDashes(value: string): string {
  return value.replace(/\s*\u2014\s*/g, " - ");
}

function forWhomLabel(t: TranslationFunction, forWhom?: string): string | null {
  if (!forWhom) return null;
  if (forWhom === "both") return t("access.programs.for_whom_both");
  if (forWhom === "her") return t("access.programs.for_whom_her");
  return t("access.programs.for_whom_him");
}

function forWhomBadgeTone(forWhom?: string): "orange" | "purple" | "blue" {
  if (forWhom === "her") return "purple";
  if (forWhom === "him") return "blue";
  return "orange";
}

type FilterOption = "all" | ProgramCategory;

const CATEGORY_ICONS: Record<ProgramCategory, LucideIcon> = {
  assessment: ClipboardCheck,
  optimization: Sparkles,
  treatment: HeartPulse,
  support: Users,
};

function getFilters(
  t: TranslationFunction,
): { key: FilterOption; label: string; icon: LucideIcon }[] {
  return [
    { key: "all", label: t("access.programs.filter_all"), icon: Layers },
    {
      key: "assessment",
      label: t("access.programs.filter_assessment"),
      icon: CATEGORY_ICONS.assessment,
    },
    {
      key: "optimization",
      label: t("access.programs.filter_optimization"),
      icon: CATEGORY_ICONS.optimization,
    },
    {
      key: "treatment",
      label: t("access.programs.filter_treatment"),
      icon: CATEGORY_ICONS.treatment,
    },
    {
      key: "support",
      label: t("access.programs.filter_support"),
      icon: CATEGORY_ICONS.support,
    },
  ];
}

const CATEGORY_ORDER: ProgramCategory[] = [
  "assessment",
  "optimization",
  "treatment",
  "support",
];

function getCategoryLabel(
  t: TranslationFunction,
  category: ProgramCategory,
): string {
  if (category === "assessment") {
    return t("access.programs.category_assessment");
  }
  if (category === "optimization") {
    return t("access.programs.category_optimization");
  }
  if (category === "treatment") return t("access.programs.category_treatment");
  return t("access.programs.category_support");
}

const PROGRAM_ILLUSTRATIONS: Record<string, ProgramCardIllustrationKind> = {
  "program-fertility-check-in": "fertility-check-in",
  "program-genetic-screening": "genetic-screening",
  "program-full-assessment": "full-assessment",
  "program-her-optimization": "her-optimization",
  "program-his-optimization": "his-optimization",
  "program-couples-optimization": "couples-optimization",
  "program-egg-freezing": "egg-freezing",
  "program-embryo-freezing": "embryo-freezing",
  "program-iui-cycle": "iui-cycle",
  "program-ivf-cycle": "ivf-cycle",
  "program-fet-cycle": "fet-cycle",
};

function ProgramCard({
  program,
  onPress,
}: {
  program: ContentBlock<"program">;
  onPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("ProgramCard");
  const { body } = program;
  const isComingSoon = body.status === "coming-soon";
  const whom = forWhomLabel(t, body.forWhom);
  const illustrationKind =
    PROGRAM_ILLUSTRATIONS[String(program.id)] ?? "generic";

  return (
    <Pressable
      onPress={() => {
        // Content-catalogue title; it varies per program, so keep a stable
        // action alongside it.
        capturePress(pressLabel(program.title), "program_card");
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={t("access.programs.open_a11y", {
        params: { title: program.title },
      })}
      style={({ pressed }) => ({
        ...(GLASS_CARD_STYLE as object),
        borderRadius: 18,
        padding: 16,
        flexDirection: "row",
        alignItems: "stretch",
        gap: 12,
        overflow: "hidden",
        backgroundColor: pressed
          ? "rgba(255, 255, 255, 0.66)"
          : "rgba(255, 255, 255, 0.42)",
        borderColor: "rgba(255, 255, 255, 0.62)",
        ...(Platform.OS === "web" && {
          transition: "transform 0.15s ease, background-color 0.15s ease",
          transform: pressed ? "translateY(1px)" : "translateY(0)",
        }),
      })}
    >
      <View
        style={{
          width: 46,
          height: 50,
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          marginTop: 1,
        }}
      >
        <ProgramCardIllustration kind={illustrationKind} size={48} />
      </View>

      <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 17,
            fontWeight: "800",
            color: colors.deepBrown,
            lineHeight: 22,
          }}
        >
          {program.title}
        </Text>
        {program.subtitle ? (
          <CardSubtitleText
            style={{
              marginTop: 0,
              fontSize: 13,
              lineHeight: 18,
              fontWeight: "500",
            }}
          >
            {withoutEmDashes(program.subtitle)}
          </CardSubtitleText>
        ) : null}
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 6,
            marginTop: 5,
          }}
        >
          {whom ? (
            <StatusBadge
              label={whom}
              tone={forWhomBadgeTone(body.forWhom)}
              icon={Users}
            />
          ) : null}
          {isComingSoon ? (
            <StatusBadge type="coming-soon" tone="neutral" />
          ) : null}
        </View>
      </View>

      <View
        style={{
          width: 88,
          flexShrink: 0,
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <ChevronRight
          size={17}
          color={EMBER.colors.textFaint}
          strokeWidth={1.7}
        />
        <View style={{ alignItems: "flex-end", gap: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              fontWeight: "700",
              color: EMBER.colors.textFaint,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            {t("access.programs.price_from")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "700",
              color: colors.greyBrown,
              lineHeight: 18,
            }}
          >
            {formatFixedPrice(body.priceRange.low, body.currency)}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

type Props = NativeStackScreenProps<AccessStackParamList, "Programs">;

/** Map fertility intent data to a recommended program ID */
function getRecommendedProgramId(
  reasons: string[],
  conditions: string[],
  hadVaricoceles: HadVaricoceles,
): ContentBlockId {
  // Egg preservation goals → egg freezing
  if (reasons.includes("more_time") || reasons.includes("no_partner")) {
    return makeId("program-egg-freezing");
  }
  // Medical conditions that benefit from optimization first
  if (
    conditions.includes("diminished_ovarian_reserve") ||
    conditions.includes("primary_ovarian_insufficiency") ||
    conditions.includes("pcos") ||
    conditions.includes("endometriosis")
  ) {
    return makeId("program-her-optimization");
  }
  // Male-factor concerns: AMAB genetic diseases or a varicocele diagnosis
  if (
    conditions.includes("klinefelter_syndrome") ||
    conditions.includes("y_chromosome_microdeletions") ||
    hadVaricoceles === "yes_diagnosed" ||
    hadVaricoceles === "yes_undiagnosed"
  ) {
    return makeId("program-couples-optimization");
  }
  // Active treatment goals
  if (reasons.includes("ivf_planned")) {
    return makeId("program-ivf-cycle");
  }
  // Default: start with assessment
  return makeId("program-fertility-check-in");
}

export default function ProgramsScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ProgramsScreen");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const [activeFilter, setActiveFilter] = useState<FilterOption>("all");

  const { data: fertilityIntent } = useFertilityIntent();
  const { data: patient } = usePatient();

  const programs = useMemo(() => getBlocksByKind("program"), []);
  const filters = useMemo(() => getFilters(t), [t]);

  const recommendedProgram = useMemo(() => {
    // Personalize from whichever profile signals exist. fertilityIntent is
    // often absent (it's only created during onboarding, and /fertility-intent
    // can 404), so don't bail on it alone — a completed medical history
    // (diseases, varicoceles) is enough to recommend a program.
    const reasons = fertilityIntent?.preservationReasons ?? [];
    const conditions = (patient?.medicalHistory?.diseases ?? [])
      .map((d) => d.disease ?? "")
      .filter((d) => !!d);
    const hadVaricoceles = patient?.medicalHistory?.hadVaricoceles ?? null;
    if (reasons.length === 0 && conditions.length === 0 && !hadVaricoceles) {
      return null;
    }
    const id = getRecommendedProgramId(reasons, conditions, hadVaricoceles);
    return (getBlock(id) as ContentBlock<"program"> | undefined) ?? null;
  }, [fertilityIntent, patient]);

  const filteredPrograms = useMemo(() => {
    if (activeFilter === "all") return programs;
    return programs.filter((p) => p.body.category === activeFilter);
  }, [programs, activeFilter]);

  const groupedPrograms = useMemo(() => {
    if (activeFilter !== "all") {
      return [
        {
          category: activeFilter as ProgramCategory,
          programs: filteredPrograms,
        },
      ];
    }
    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      programs: filteredPrograms.filter((p) => p.body.category === cat),
    })).filter((g) => g.programs.length > 0);
  }, [filteredPrograms, activeFilter]);

  const onItemPress = useCallback(
    (item: ContentBlock<"program">) => {
      navigation.navigate("ProgramDetail", { programId: item.id });
    },
    [navigation],
  );

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab
                  ? t("you.shared.back")
                  : t("access.programs.back"),
              ),
              "back",
            );
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {route.params?.returnTab
              ? t("you.shared.back")
              : t("access.programs.back")}
          </Text>
        </Pressable>

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("access.programs.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 16,
            lineHeight: 21,
          }}
        >
          {t("access.programs.subtitle")}
        </Text>

        {/* Recommended hero card */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                recommendedProgram?.title ??
                  t("access.programs.hero_fallback_title"),
              ),
              "recommended_program_hero",
            );
            if (recommendedProgram) {
              onItemPress(recommendedProgram);
            }
          }}
          style={{
            borderRadius: 24,
            overflow: "hidden",
            height: 200,
            marginBottom: 20,
          }}
        >
          <Image
            source={require("../../../assets/images/dark-copper-texture.png")}
            style={{
              position: "absolute",
              width: "120%",
              height: "120%",
              left: "-10%",
              top: "-10%",
            }}
            resizeMode="cover"
          />
          <View
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: 24,
              borderWidth: 0.5,
              borderColor: "rgba(255, 255, 255, 0.12)",
            }}
          />
          <View
            style={{
              flex: 1,
              padding: 24,
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                alignSelf: "flex-start",
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 999,
                backgroundColor: "rgba(255,255,255,0.15)",
              }}
            >
              <Sparkles
                size={11}
                color="rgba(255,255,255,0.7)"
                strokeWidth={2}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: "rgba(255,255,255,0.7)",
                  letterSpacing: 0.3,
                }}
              >
                {recommendedProgram
                  ? t("access.programs.hero_recommended_eyebrow")
                  : t("access.programs.hero_find_eyebrow")}
              </Text>
            </View>
            <View style={{ gap: 6 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 22,
                  fontWeight: "700",
                  color: "#FFFFFF",
                  lineHeight: 28,
                }}
              >
                {recommendedProgram?.title ??
                  t("access.programs.hero_fallback_title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 19,
                }}
              >
                {recommendedProgram?.subtitle
                  ? withoutEmDashes(recommendedProgram.subtitle)
                  : t("access.programs.hero_fallback_subtitle")}
              </Text>
            </View>
          </View>
        </Pressable>

        {/* Category filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 8, marginBottom: 20 }}
        >
          {filters.map((filter) => {
            const isActive = activeFilter === filter.key;
            return (
              <MoonFilterChip
                key={filter.key}
                label={filter.label}
                icon={filter.icon}
                selected={isActive}
                onPress={() => setActiveFilter(filter.key)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Grouped programs */}
        {groupedPrograms.map((group) => (
          <View key={group.category} style={{ marginBottom: 24 }}>
            {/* Section header */}
            <View style={{ marginBottom: 12 }}>
              <SectionTitle icon={CATEGORY_ICONS[group.category]}>
                {getCategoryLabel(t, group.category)}
              </SectionTitle>
            </View>

            {/* Program cards */}
            <View style={{ gap: 14 }}>
              {group.programs.map((program) => (
                <ProgramCard
                  key={program.id}
                  program={program}
                  onPress={() => onItemPress(program)}
                />
              ))}
            </View>
          </View>
        ))}
      </ScrollView>
    </GlassScreenLayout>
  );
}
